import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuthStore } from '@/stores/authStore';
import Navbar from '@/components/Navbar';
import CampaignHeader from '@/components/CampaignHeader';
import BriefSection from '@/components/BriefSection';
import StatsSection from '@/components/StatsSection';
import { ProjectCard } from '@/components/ProjectCard';

interface PackageSlot {
  slot_id: string;
  project_id: string;
  show_id: string;
  slot_type: string;
  placement_window: string;
  compensation: number;
}

interface PackageShow {
  id: string;
  title: string;
  logline?: string;
  director?: string;
  writer?: string;
  genre?: string;
  status?: string;
  production_status?: string;
  platform_metrics?: {
    total_followers?: number;
    total_views?: number;
    total_subscribers?: number;
  };
  demographics?: {
    age_range?: string;
    gender_split?: string;
    top_locations?: string[];
  };
  hero_image?: string;
  slots: PackageSlot[];
  created_at?: string;
  updated_at?: string;
}

interface Package {
  id: string;
  name: string;
  description: string;
  status: string;
  brand_id: string;
  campaign_id: string;
  brand_name?: string;
  campaign_name?: string;
  anchor_projects?: PackageShow[];
  fill_projects?: PackageShow[];
  anchor_shows?: PackageShow[];
  fill_shows?: PackageShow[];
  total_reach: number;
  total_cost?: number;
  total_budget?: number;
  placement_window?: string;
  targeting?: {
    target_ecpm_range?: {
      min: number;
      max: number;
    };
  };
  target_ecpm_range?: {
    min: number;
    max: number;
  };
  estimated_cpm_range?: {
    low: number;
    mid?: number;
    high: number;
  };
  created_at: string;
  updated_at: string;
}

interface Campaign {
  id: string;
  name: string;
  description: string;
  budget: number;
  status: string;
  window_details?: {
    start_date: string;
    end_date: string;
  };
  requirements: {
    tone_preferences: string[];
    target_categories: string[];
    suitability_filters: string[];
    budget_usd?: number;
    target_ecpm?: number;
  };
}

export default function BrowsePackagesPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { brand } = useAuthStore();
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [brandApproval, setBrandApproval] = useState(false);

  // Fetch campaign data
  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => apiClient.getCampaign(campaignId!),
    enabled: !!campaignId,
  });

  // Check if campaign has a hold placed and redirect
  useEffect(() => {
    if (campaign && campaign.status === 'hold') {
      navigate(`/campaigns/${campaignId}/hold-placed`);
    }
  }, [campaign, campaignId, navigate]);

  // Fetch packages data using React Query - use ops-admin endpoint with campaign_id filter
  const { data: packagesData = [], isLoading: isLoadingPackages } = useQuery({
    queryKey: ['campaignPackages', campaignId],
    queryFn: async () => {
      // Use the ops-admin packages endpoint with campaign_id parameter for server-side filtering
      const response = await apiClient.get<{packages: Package[]}>(`/api/v1/ops-admin/packages?campaign_id=${campaignId}&limit=100`);
      const packages = response.packages || [];
      
      // Only show active/approved packages in the UI
      const activePackages = packages.filter((pkg: Package) =>
        pkg.status === 'approved' || pkg.status === 'active'
      );
      
      return activePackages;
    },
    enabled: !!campaignId,
  });

  const packages = packagesData;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const calculateEstimatedCPM = (budget: number, reach: number) => {
    if (reach === 0) return 0;
    return (budget / reach) * 1000;
  };

  const getPackageCPMRange = (pkg: Package) => {
    // First try to use the target_ecpm_range from package targeting data
    if (pkg.targeting?.target_ecpm_range && pkg.targeting.target_ecpm_range.min && pkg.targeting.target_ecpm_range.max) {
      return {
        min: pkg.targeting.target_ecpm_range.min,
        max: pkg.targeting.target_ecpm_range.max,
        display: `$${pkg.targeting.target_ecpm_range.min.toFixed(0)}-$${pkg.targeting.target_ecpm_range.max.toFixed(0)}`
      };
    }
    
    // Fallback to direct target_ecpm_range if available
    if (pkg.target_ecpm_range && pkg.target_ecpm_range.min && pkg.target_ecpm_range.max) {
      return {
        min: pkg.target_ecpm_range.min,
        max: pkg.target_ecpm_range.max,
        display: `$${pkg.target_ecpm_range.min.toFixed(0)}-$${pkg.target_ecpm_range.max.toFixed(0)}`
      };
    }
    
    // Fallback to estimated_cpm_range if available
    if (pkg.estimated_cpm_range && pkg.estimated_cpm_range.low && pkg.estimated_cpm_range.high) {
      return {
        min: pkg.estimated_cpm_range.low,
        max: pkg.estimated_cpm_range.high,
        display: `$${pkg.estimated_cpm_range.low.toFixed(0)}-$${pkg.estimated_cpm_range.high.toFixed(0)}`
      };
    }
    
    // Try campaign target_ecpm if available
    const campaignTargetEcpm = campaign?.requirements?.target_ecpm;
    if (campaignTargetEcpm && campaignTargetEcpm > 0) {
      return {
        min: campaignTargetEcpm,
        max: campaignTargetEcpm,
        display: `$${campaignTargetEcpm.toFixed(2)}`
      };
    }
    
    // Final fallback to calculated CPM
    const calculatedCPM = calculateEstimatedCPM(pkg.total_budget || pkg.total_cost || 0, pkg.total_reach || 0);
    return {
      min: calculatedCPM,
      max: calculatedCPM,
      display: calculatedCPM > 0 ? `$${calculatedCPM.toFixed(2)}` : '$0.00'
    };
  };

  const getAggregatedCPMRange = (packages: Package[]) => {
    if (packages.length === 0) {
      // If no packages but we have campaign target_ecpm, use that
      const campaignTargetEcpm = campaign?.requirements?.target_ecpm;
      if (campaignTargetEcpm && campaignTargetEcpm > 0) {
        return { min: campaignTargetEcpm, max: campaignTargetEcpm, display: `$${campaignTargetEcpm.toFixed(2)}` };
      }
      return { min: 0, max: 0, display: '$0.00' };
    }
    
    const cpmRanges = packages.map(pkg => getPackageCPMRange(pkg));
    const minCPM = Math.min(...cpmRanges.map(range => range.min));
    const maxCPM = Math.max(...cpmRanges.map(range => range.max));
    
    if (minCPM === maxCPM && minCPM > 0) {
      return { min: minCPM, max: maxCPM, display: `$${minCPM.toFixed(2)}` };
    } else if (minCPM > 0 && maxCPM > 0) {
      return { min: minCPM, max: maxCPM, display: `$${minCPM.toFixed(0)}-$${maxCPM.toFixed(0)}` };
    } else {
      // If all packages return 0, try campaign target_ecpm as final fallback
      const campaignTargetEcpm = campaign?.requirements?.target_ecpm;
      if (campaignTargetEcpm && campaignTargetEcpm > 0) {
        return { min: campaignTargetEcpm, max: campaignTargetEcpm, display: `$${campaignTargetEcpm.toFixed(2)}` };
      }
      return { min: 0, max: 0, display: '$0.00' };
    }
  };

  const getAllProjects = (pkg: Package) => {
    const anchorProjects = pkg.anchor_projects || pkg.anchor_shows || [];
    const fillProjects = pkg.fill_projects || pkg.fill_shows || [];
    return [...anchorProjects, ...fillProjects];
  };

  const handleViewProjectDetails = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handlePlaceHold = (pkg?: Package) => {
    // If no package is provided, use the first available package
    const packageToHold = pkg || packages[0];
    if (packageToHold) {
      setSelectedPackage(packageToHold);
      setShowHoldModal(true);
    }
  };

  const handleConfirmHold = async () => {
    if (!selectedPackage) return;
    
    try {
      console.log('Placing hold on package:', selectedPackage.id);
      console.log('Brand approval:', brandApproval);
      
      // Update campaign status to 'hold' via API call
      await apiClient.updateCampaign(campaignId!, { status: 'hold' });
      
      // Close modal and reset state
      setShowHoldModal(false);
      setSelectedPackage(null);
      setBrandApproval(false);
      
      // Redirect to hold placed page
      navigate(`/campaigns/${campaignId}/hold-placed`);
    } catch (error) {
      console.error('Error placing hold:', error);
      alert('Failed to place hold. Please try again.');
    }
  };

  const handleCancelHold = () => {
    setShowHoldModal(false);
    setSelectedPackage(null);
    setBrandApproval(false);
  };

  if (campaignLoading) {
    return (
      <div className="min-h-screen bg-white font-geist">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 mx-auto"></div>
            <p className="mt-4 text-neutral-600">Loading campaign...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-white font-geist">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">Campaign not found</h2>
            <p className="text-neutral-600 mb-4">The campaign you're looking for doesn't exist or you don't have access to it.</p>
            <Button 
              onClick={() => navigate('/brand-dashboard')}
              className="bg-neutral-900 text-white hover:bg-neutral-800"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate aggregate stats for all packages
  const totalPlacements = packages.reduce((sum, pkg) => {
    const anchorSlots = (pkg.anchor_projects || pkg.anchor_shows || []).reduce((slotSum, project) =>
      slotSum + ((project as any).slots?.length || 0), 0);
    const fillSlots = (pkg.fill_projects || pkg.fill_shows || []).reduce((slotSum, project) =>
      slotSum + ((project as any).slots?.length || 0), 0);
    return sum + anchorSlots + fillSlots;
  }, 0);

  // Calculate narrative and product placement counts
  const narrativeCount = packages.reduce((sum, pkg) => {
    const anchorNarrative = (pkg.anchor_projects || pkg.anchor_shows || []).reduce((slotSum, project) =>
      slotSum + ((project as any).slots?.filter((slot: any) => slot.slot_type === 'narrative_integration').length || 0), 0);
    const fillNarrative = (pkg.fill_projects || pkg.fill_shows || []).reduce((slotSum, project) =>
      slotSum + ((project as any).slots?.filter((slot: any) => slot.slot_type === 'narrative_integration').length || 0), 0);
    return sum + anchorNarrative + fillNarrative;
  }, 0);

  const productCount = packages.reduce((sum, pkg) => {
    const anchorProduct = (pkg.anchor_projects || pkg.anchor_shows || []).reduce((slotSum, project) =>
      slotSum + ((project as any).slots?.filter((slot: any) => slot.slot_type === 'product_placement').length || 0), 0);
    const fillProduct = (pkg.fill_projects || pkg.fill_shows || []).reduce((slotSum, project) =>
      slotSum + ((project as any).slots?.filter((slot: any) => slot.slot_type === 'product_placement').length || 0), 0);
    return sum + anchorProduct + fillProduct;
  }, 0);
  
  const totalProjects = packages.reduce((sum, pkg) => {
    const uniqueProjects = new Set([
      ...(pkg.anchor_projects || pkg.anchor_shows || []).map(p => (p as any).id || (p as any).project_id),
      ...(pkg.fill_projects || pkg.fill_shows || []).map(p => (p as any).id || (p as any).project_id)
    ]);
    return sum + uniqueProjects.size;
  }, 0);
  
  const anchorProjects = packages.flatMap(pkg => pkg.anchor_projects || pkg.anchor_shows || []);
  const fillProjects = packages.flatMap(pkg => pkg.fill_projects || pkg.fill_shows || []);
  
  // Calculate reach from individual project platform metrics
  const getProjectReach = (project: any) => {
    const metrics = project.platform_metrics;
    if (metrics) {
      return (metrics.total_followers || 0) + (metrics.total_views || 0) + (metrics.total_subscribers || 0);
    }
    // Fallback to package reach divided by number of projects if no individual metrics
    return 0;
  };
  
  const anchorReach = anchorProjects.reduce((sum, project) => sum + getProjectReach(project), 0);
  const fillReach = fillProjects.reduce((sum, project) => sum + getProjectReach(project), 0);
  const totalEstimatedViews = anchorReach + fillReach;
  const totalBudget = packages.reduce((sum, pkg) => sum + (pkg.total_budget || pkg.total_cost || 0), 0);
  const estimatedCPM = calculateEstimatedCPM(totalBudget, totalEstimatedViews);
  const aggregatedCPMRange = getAggregatedCPMRange(packages);


  return (
    <div className="min-h-screen bg-white font-geist">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <CampaignHeader
          packageName={campaign.name}
          placementWindow={campaign.window_details}
          campaignId={campaignId}
          onPlaceHold={() => handlePlaceHold()}
        />

        <BriefSection
          budget={campaign.requirements?.budget_usd || campaign.budget || 0}
          tonePreferences={campaign.requirements?.suitability_filters || campaign.requirements?.tone_preferences || []}
          targetCategories={campaign.requirements?.preferred_verticals || campaign.requirements?.target_categories || []}
        />

        <StatsSection
          totalPlacements={totalPlacements}
          narrativeCount={narrativeCount}
          productCount={productCount}
          totalProjects={totalProjects}
          anchorCount={anchorProjects.length}
          supportingCount={fillProjects.length}
          totalReach={totalEstimatedViews}
          anchorReach={anchorReach}
          supportingReach={fillReach}
          budget={totalBudget}
          cpmRange={aggregatedCPMRange}
        />
        

        {/* Package Projects */}
        <div className="space-y-8">
          {isLoadingPackages ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 mb-4"></div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">Loading packages...</h3>
              <p className="text-neutral-600">
                Fetching approved packages for this campaign.
              </p>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">No approved packages available</h3>
              <p className="text-neutral-600 mb-4">
                There are no approved packages for this campaign yet.
                {campaignId && (
                  <span className="block mt-2 text-sm">
                    If packages have been created, they may be pending approval in the admin dashboard.
                  </span>
                )}
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => navigate(`/campaigns/${campaignId}/browse-eligible`)}
                  className="bg-neutral-900 text-white hover:bg-neutral-800"
                >
                  Browse Eligible Projects
                </Button>
                <Button
                  onClick={() => navigate('/admin/ops-dashboard')}
                  variant="outline"
                  className="border-neutral-300 hover:bg-neutral-50"
                >
                  Admin Dashboard
                </Button>
              </div>
            </div>
          ) : (
            packages.map((pkg) => {
              const anchorProjects = pkg.anchor_projects || pkg.anchor_shows || [];
              const fillProjects = pkg.fill_projects || pkg.fill_shows || [];
              
              return (
                <div key={pkg.id} className="space-y-6">
                  <div className="border-b border-neutral-200 pb-4">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-2">{pkg.name}</h3>
                    <p className="text-neutral-600">{pkg.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-neutral-500">
                      <span>Budget: {formatCurrency(pkg.total_budget || pkg.total_cost || 0)}</span>
                      <span>Reach: {formatNumber(pkg.total_reach || 0)}</span>
                      <span>Projects: {getAllProjects(pkg).length}</span>
                    </div>
                  </div>

                  {/* Anchor Projects */}
                  {anchorProjects.length > 0 && (
                    <div>
                      <div className="mb-6">
                        <h4 className="text-2xl font-bold text-neutral-900 mb-2">Anchor Projects</h4>
                        <p className="text-neutral-600 mb-4">Core projects selected for strategic alignment and placement depth.</p>
                        <div className="text-sm text-neutral-600">
                          {anchorProjects.length} Projects • {anchorProjects.reduce((sum, project) => sum + ((project as any).slots?.length || 0), 0)} Placements • {anchorProjects.reduce((sum, project) => sum + ((project as any).slots?.filter((slot: any) => slot.slot_type === 'narrative_integration').length || 0), 0)} Narrative • {anchorProjects.reduce((sum, project) => sum + ((project as any).slots?.filter((slot: any) => slot.slot_type === 'product_placement').length || 0), 0)} Product
                        </div>
                      </div>
                      <div className="space-y-6">
                        {anchorProjects.map((project) => {
                          const projectData = project as any;
                          return (
                            <ProjectCard
                              key={projectData.id || projectData.project_id}
                              project={{
                                id: projectData.id || projectData.project_id,
                                title: projectData.title || projectData.project_name || projectData.show_name,
                                logline: projectData.logline,
                                director: projectData.director || projectData.creator,
                                writer: projectData.writer,
                                genre: projectData.genre || projectData.vertical,
                                status: projectData.status,
                                production_status: projectData.production_status,
                                platform_metrics: projectData.platform_metrics,
                                demographics: projectData.demographics,
                                hero_image: projectData.hero_image || projectData.hero_image_url,
                                created_at: projectData.created_at,
                                updated_at: projectData.updated_at,
                                slots: projectData.slots || projectData.available_slots || projectData.assigned_slots || [],
                                team: projectData.team,
                                awards: projectData.awards,
                                tags: projectData.tags,
                              }}
                              type="anchor"
                              onViewDetails={handleViewProjectDetails}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Fill Projects - Supporting Projects Table */}
                  {fillProjects.length > 0 && (
                    <div>
                      <div className="mb-6">
                        <h4 className="text-2xl font-bold text-neutral-900 mb-2">Supporting Projects</h4>
                        <p className="text-neutral-600 mb-4">Complementary placements that extend reach and frequency across more projects.</p>
                        <div className="text-sm text-neutral-600">
                          {fillProjects.length} Projects • {fillProjects.reduce((sum, project) => sum + ((project as any).slots?.length || 0), 0)} Placements • {fillProjects.reduce((sum, project) => sum + ((project as any).slots?.filter((slot: any) => slot.slot_type === 'narrative_integration').length || 0), 0)} Narrative • {fillProjects.reduce((sum, project) => sum + ((project as any).slots?.filter((slot: any) => slot.slot_type === 'product_placement').length || 0), 0)} Product
                        </div>
                      </div>
                      
                      {/* Table Layout */}
                      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-neutral-50 border-b border-neutral-200">
                              <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">TITLE</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">CREATOR(S)</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">VERTICAL</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">PLACEMENT</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">UNITS</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">EST. VIEWS</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-200">
                              {fillProjects.map((project, index) => {
                                const projectData = project as any;
                                const slots = projectData.slots || projectData.available_slots || projectData.assigned_slots || [];
                                const narrativeSlots = slots.filter((slot: any) => slot.slot_type === 'narrative_integration').length;
                                const productSlots = slots.filter((slot: any) => slot.slot_type === 'product_placement').length;
                                const totalSlots = slots.length;
                                
                                // Calculate estimated views from platform metrics
                                const estimatedViews = projectData.platform_metrics ?
                                  (projectData.platform_metrics.total_followers || 0) +
                                  (projectData.platform_metrics.total_views || 0) +
                                  (projectData.platform_metrics.total_subscribers || 0) : 0;
                                
                                return (
                                  <tr key={projectData.id || projectData.project_id} className="hover:bg-neutral-50">
                                    <td className="px-6 py-4">
                                      <div className="text-sm font-medium text-neutral-900">
                                        {projectData.title || projectData.project_name || projectData.show_name}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-neutral-900">
                                        {projectData.director || projectData.creator || 'N/A'}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-neutral-900">
                                        {projectData.genre || projectData.vertical || 'N/A'}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-neutral-900">
                                        {narrativeSlots > 0 && productSlots > 0
                                          ? `${narrativeSlots} Narrative • ${productSlots} Product`
                                          : narrativeSlots > 0
                                            ? `${narrativeSlots} Narrative`
                                            : productSlots > 0
                                              ? `${productSlots} Product`
                                              : 'N/A'
                                        }
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-neutral-900">
                                        {totalSlots}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-neutral-900">
                                        {formatNumber(estimatedViews)}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      {/* Note at bottom */}
                      <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          <strong>All brands receive packages simultaneously.</strong> Packages are first come, first served. Place a hold to secure your selection.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Place Hold Confirmation Modal */}
      <Dialog open={showHoldModal} onOpenChange={setShowHoldModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Confirm Your Hold</DialogTitle>
          </DialogHeader>
          
          {selectedPackage && (
            <div className="space-y-6">
              <div>
                <p className="text-neutral-600 mb-2">You're about to place a hold on this package.</p>
                <p className="text-neutral-600">Creators will be notified and a 48-hour approval window will begin.</p>
              </div>

              {/* Package Info - Responsive Grid */}
              <div className="bg-neutral-50 rounded-lg p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-6">
                  <div>
                    <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">PACKAGE</p>
                    <p className="text-lg sm:text-xl font-semibold text-neutral-900 break-words">{selectedPackage.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">BUDGET</p>
                    <p className="text-lg sm:text-xl font-semibold text-neutral-900">{formatCurrency(selectedPackage.total_budget || selectedPackage.total_cost || 0)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">PROJECTS</p>
                    <p className="text-lg sm:text-xl font-semibold text-neutral-900">{getAllProjects(selectedPackage).length}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">PLACEMENTS</p>
                    <p className="text-lg sm:text-xl font-semibold text-neutral-900">
                      {getAllProjects(selectedPackage).reduce((sum, project) => sum + ((project as any).slots?.length || 0), 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">EST. CPM</p>
                    <p className="text-lg sm:text-xl font-semibold text-neutral-900">
                      {getPackageCPMRange(selectedPackage).display}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">EST. VIEWS</p>
                    <p className="text-lg sm:text-xl font-semibold text-neutral-900">{formatNumber(selectedPackage.total_reach || 0)}</p>
                  </div>
                </div>
              </div>

              {/* Process Steps - Responsive Layout */}
              <div>
                <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-4">WHAT HAPPENS AFTER YOU CONFIRM</p>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-neutral-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0">1</div>
                    <p className="text-sm sm:text-base text-neutral-700 pt-0.5 sm:pt-1">Creators are notified (48 hours to respond)</p>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-neutral-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0">2</div>
                    <p className="text-sm sm:text-base text-neutral-700 pt-0.5 sm:pt-1">We return a finalized list of confirmed placements</p>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-neutral-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0">3</div>
                    <p className="text-sm sm:text-base text-neutral-700 pt-0.5 sm:pt-1">You receive a contract</p>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-neutral-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0">4</div>
                    <p className="text-sm sm:text-base text-neutral-700 pt-0.5 sm:pt-1">2-week creative alignment window begins</p>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-neutral-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0">5</div>
                    <p className="text-sm sm:text-base text-neutral-700 pt-0.5 sm:pt-1">Final contract is issued</p>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-yellow-800">
                  Project placements may vary based on production schedules and confirmations. All replacements will meet the same campaign requirements.
                </p>
              </div>

              {/* Approval Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="brand-approval"
                  checked={brandApproval}
                  onCheckedChange={(checked) => setBrandApproval(checked === true)}
                />
                <label htmlFor="brand-approval" className="text-xs sm:text-sm text-neutral-700 leading-relaxed">
                  I approve sharing {brand?.name || brand?.advertiser || 'Brand Name'}{brand?.division ? ` & ${brand.division}` : ''} with the creators in this package.
                </label>
              </div>

              {/* Action Buttons - Responsive */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancelHold}
                  className="flex-1 hover:bg-neutral-50 transition-colors duration-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmHold}
                  disabled={!brandApproval}
                  className="flex-1 bg-neutral-900 text-white hover:bg-neutral-800 disabled:bg-neutral-300 transition-colors duration-200"
                >
                  Place Hold
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Package Summary Footer */}
      <div className="bg-white py-6 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-neutral-600 mb-2">Have a question?
              <button
                className="text-neutral-900 underline ml-1 hover:text-neutral-700 transition-colors duration-200"
                onClick={() => {
                  // Open email client with pre-filled subject and body
                  const subject = encodeURIComponent(`Question about Campaign: ${campaign?.name || 'Package Campaign'}`);
                  const body = encodeURIComponent(`Hi Artie team,\n\nI have a question about my campaign package:\n\nCampaign: ${campaign?.name || 'N/A'}\nCampaign ID: ${campaignId || 'N/A'}\n\nQuestion:\n\n\nBest regards`);
                  window.location.href = `mailto:support@artie.com?subject=${subject}&body=${body}`;
                }}
              >
                Send us a message
              </button>
            </p>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-neutral-500">
              © 2026 Artie. All rights reserved.
            </div>
            
            <div className="flex items-center gap-4">
              {/* Social Media Icons */}
              <a href="#" className="text-neutral-500 hover:text-neutral-900">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="#" className="text-neutral-500 hover:text-neutral-900">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Package Summary Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-neutral-900 text-white py-4 shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <h3 className="text-lg font-semibold">Your Package Summary</h3>
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-neutral-400 uppercase tracking-wider">PLACEMENTS</span>
                  <span className="ml-2 font-semibold">{totalPlacements}</span>
                </div>
                <div>
                  <span className="text-neutral-400 uppercase tracking-wider">PROJECTS</span>
                  <span className="ml-2 font-semibold">{totalProjects}</span>
                </div>
                <div>
                  <span className="text-neutral-400 uppercase tracking-wider">EST. VIEWS</span>
                  <span className="ml-2 font-semibold">{formatNumber(totalEstimatedViews)}</span>
                </div>
                <div>
                  <span className="text-neutral-400 uppercase tracking-wider">EST. CPM</span>
                  <span className="ml-2 font-semibold">{aggregatedCPMRange.display}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="bg-transparent border-neutral-600 text-white hover:bg-neutral-800 hover:border-neutral-500 transition-all duration-200"
                onClick={() => {
                  // Export functionality - generate and download one-sheet
                  const packageData = {
                    campaignName: campaign?.name || 'Campaign',
                    totalPlacements,
                    totalProjects,
                    totalEstimatedViews,
                    estimatedCPM: aggregatedCPMRange.display,
                    totalBudget,
                    packages: packages.map(pkg => ({
                      name: pkg.name,
                      description: pkg.description,
                      budget: pkg.total_budget || pkg.total_cost || 0,
                      reach: pkg.total_reach,
                      projects: getAllProjects(pkg).length,
                      cpmRange: getPackageCPMRange(pkg).display
                    }))
                  };
                  
                  // Create and download CSV
                  const csvContent = [
                    ['Campaign Name', 'Total Placements', 'Total Projects', 'Est. Views', 'Est. CPM', 'Total Budget'],
                    [packageData.campaignName, packageData.totalPlacements, packageData.totalProjects, packageData.totalEstimatedViews, packageData.estimatedCPM, `$${packageData.totalBudget}`],
                    [],
                    ['Package Details'],
                    ['Package Name', 'Description', 'Budget', 'Reach', 'Projects', 'CPM Range'],
                    ...packageData.packages.map(pkg => [pkg.name, pkg.description, `$${pkg.budget}`, pkg.reach, pkg.projects, pkg.cpmRange])
                  ].map(row => row.join(',')).join('\n');
                  
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${packageData.campaignName.replace(/[^a-zA-Z0-9]/g, '_')}_one_sheet.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                }}
              >
                Export One-Sheet
              </Button>
              <Button
                onClick={() => handlePlaceHold()}
                className="bg-white text-neutral-900 hover:bg-neutral-100 font-semibold"
              >
                Place Hold on Package
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Add bottom padding to account for sticky footer */}
      <div className="h-20"></div>
    </div>
  );
}